import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ISbStory } from '@storyblok/js';
import { BillingScope } from '../../../billing-consts';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { sleep } from '../../../lib/sleep';
import { ProductNameShortPipe } from '../../../pipes/product-name-short.pipe';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { PricingService } from '../../../services/pricing.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { StoryblokCdnPipe } from '../../../storyblok/pipes/storyblok-cdn.pipe';
import { StoryblokRichTextPipe } from '../../../storyblok/storyblok-rich-text.pipe';
import { StoryblokImage } from '../../../storyblok/storyblok-types';
import { CheckoutDialogComponent } from '../checkout-dialog/checkout-dialog.component';

@Component({
  selector: 'app-feature-paywall-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './feature-paywall-dialog.component.html',
  styleUrls: ['./feature-paywall-dialog.component.scss'],
  imports: [ButtonDirective, BadgeComponent, StoryblokRichTextPipe, ProductNameShortPipe, StoryblokCdnPipe],
})
export class FeaturePaywallDialogComponent implements OnGetData {
  private pricingService = inject(PricingService);
  private permissionService = inject(PermissionService);
  private subscriptionServcie = inject(SubscriptionService);
  private dialog = inject(DialogService);
  dialogName = 'feature-paywall';
  @ViewChild('videoEl') videoEl: ElementRef<HTMLVideoElement>;

  subscription = toSignal(this.subscriptionServcie.getSubscription());
  canStartTrial = computed(() => this.pricingService.canStartTrial(this.subscription()));
  productName = signal<string>(undefined);
  scope = signal<BillingScope>(undefined);
  title = signal<string>('');
  description = signal<string>('');
  asset = signal<StoryblokImage>(undefined);
  badgeColor = computed(() => {
    const product = this.productName();
    if (product === 'enterprise') return 'grey-80';
    return 'primary';
  });

  dimensions = computed(() => {
    const asset = this.asset();
    if (!asset) return { width: 0, height: 0 };
    const url = asset.filename;
    return {
      width: parseInt(url.split('/')[5].split('x')[0]),
      height: parseInt(url.split('/')[5].split('x')[1]),
    };
  });

  dialogOptions(): DialogOptions {
    return { size: 480, spacingX: 0, spacingY: 0 };
  }
  onGetData(data: { content: ISbStory['data']['story']['content'] }) {
    this.scope.set(data.content.scope);
    this.title.set(data.content.title);
    this.description.set(data.content.description);
    this.asset.set(data.content.asset);
    this.permissionService.getMinPlanForScope(this.scope()).subscribe(productName => {
      this.productName.set(productName);
    });
    this.handleVideo();
  }

  confirm() {
    this.dialog.open(CheckoutDialogComponent, { scope: this.scope() });
  }

  private async handleVideo() {
    await sleep(0);
    if (!this.videoEl) return;
    this.videoEl.nativeElement.muted = true;
    try {
      await this.videoEl.nativeElement.play();
    } catch (e) {
      console.error(e);
    }
  }
}
